import React from 'react';

import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import type { OwnerWorkspaceFilterOptions } from 'config/api/assetRopa/assetRopaWorkspace.helpers';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';
import type { OwnerWorkspaceFilters } from '../useOwnerAssetRopaWorkspace';

type CompletenessMessageKey = keyof typeof processing_activities_messages.asset_ropa.owner_workspace.completeness_status;
type GdprRelevanceMessageKey = keyof typeof processing_activities_messages.asset_ropa.owner_workspace.gdpr_relevance;

const COMPLETENESS_OPTIONS: CompletenessMessageKey[] = ['complete', 'incomplete', 'no_processing_activities'];
const GDPR_RELEVANCE_OPTIONS: GdprRelevanceMessageKey[] = ['currently_indicated', 'no_longer_indicated', 'all'];

type Props = {
  filterOptions: OwnerWorkspaceFilterOptions;
  filters: OwnerWorkspaceFilters;
  onFiltersChange: (filters: OwnerWorkspaceFilters) => void;
};

const OwnerWorkspaceFiltersToolbar: React.FC<Props> = ({ filterOptions, filters, onFiltersChange }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.owner_workspace.filters;
  const filterMessages = processing_activities_messages.asset_ropa.filters;

  const updateFilter = <K extends keyof OwnerWorkspaceFilters>(key: K, value: OwnerWorkspaceFilters[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  return (
    <div className={styles.searchRow}>
      <TextField
        className={styles.searchInput}
        label={t(messages.search_placeholder)}
        onChange={event => updateFilter('search', event.target.value)}
        type='search'
        value={filters.search}
        variant='outlined'
      />
      <div className={styles.filtersRow}>
        <FormControl className={styles.filterSelect} size='small' variant='outlined'>
          <InputLabel>{t(messages.asset_type)}</InputLabel>
          <Select
            label={t(messages.asset_type)}
            onChange={event => updateFilter('assetTypeId', event.target.value as OwnerWorkspaceFilters['assetTypeId'])}
            value={filters.assetTypeId}
          >
            <MenuItem value=''>{t(messages.all)}</MenuItem>
            {filterOptions.assetTypes.map(assetType => (
              <MenuItem key={assetType.id} value={assetType.id}>
                {assetType.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className={styles.filterSelect} size='small' variant='outlined'>
          <InputLabel>{t(messages.completeness)}</InputLabel>
          <Select
            label={t(messages.completeness)}
            onChange={event => updateFilter('completeness', event.target.value as OwnerWorkspaceFilters['completeness'])}
            value={filters.completeness}
          >
            <MenuItem value=''>{t(messages.all)}</MenuItem>
            {COMPLETENESS_OPTIONS.map(status => (
              <MenuItem key={status} value={status}>
                {t(processing_activities_messages.asset_ropa.owner_workspace.completeness_status[status])}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className={styles.filterSelect} size='small' variant='outlined'>
          <InputLabel>{t(messages.gdpr_relevance)}</InputLabel>
          <Select
            label={t(messages.gdpr_relevance)}
            onChange={event => updateFilter('gdprRelevance', event.target.value as OwnerWorkspaceFilters['gdprRelevance'])}
            value={filters.gdprRelevance}
          >
            {GDPR_RELEVANCE_OPTIONS.map(status => (
              <MenuItem key={status} value={status}>
                {t(processing_activities_messages.asset_ropa.owner_workspace.gdpr_relevance[status])}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className={styles.filterSelect} size='small' variant='outlined'>
          <InputLabel>{t(filterMessages.dpia_state)}</InputLabel>
          <Select
            label={t(filterMessages.dpia_state)}
            onChange={event => updateFilter('dpiaState', event.target.value as OwnerWorkspaceFilters['dpiaState'])}
            value={filters.dpiaState}
          >
            <MenuItem value=''>{t(messages.all)}</MenuItem>
            <MenuItem value='indicated'>{t(filterMessages.dpia_indicated)}</MenuItem>
            <MenuItem value='none'>{t(filterMessages.dpia_none)}</MenuItem>
          </Select>
        </FormControl>
        <FormControl className={styles.filterSelect} size='small' variant='outlined'>
          <InputLabel>{t(filterMessages.sensitive_personal_data)}</InputLabel>
          <Select
            label={t(filterMessages.sensitive_personal_data)}
            onChange={event => updateFilter('sensitivePersonalData', event.target.value as OwnerWorkspaceFilters['sensitivePersonalData'])}
            value={filters.sensitivePersonalData}
          >
            <MenuItem value=''>{t(messages.all)}</MenuItem>
            <MenuItem value='true'>{t(filterMessages.yes)}</MenuItem>
            <MenuItem value='false'>{t(filterMessages.no)}</MenuItem>
          </Select>
        </FormControl>
        <FormControl className={styles.filterSelect} size='small' variant='outlined'>
          <InputLabel>{t(filterMessages.third_country_processing)}</InputLabel>
          <Select
            label={t(filterMessages.third_country_processing)}
            onChange={event =>
              updateFilter('thirdCountryProcessing', event.target.value as OwnerWorkspaceFilters['thirdCountryProcessing'])
            }
            value={filters.thirdCountryProcessing}
          >
            <MenuItem value=''>{t(messages.all)}</MenuItem>
            <MenuItem value='true'>{t(filterMessages.yes)}</MenuItem>
            <MenuItem value='false'>{t(filterMessages.no)}</MenuItem>
          </Select>
        </FormControl>
        <div className={styles.filterSelectCell}>
          <Button
            onClick={() =>
              onFiltersChange({
                ...filters,
                search: '',
                assetTypeId: '',
                completeness: '',
                dpiaState: '',
                sensitivePersonalData: '',
                thirdCountryProcessing: '',
              })
            }
            variant='outlined'
          >
            {t(messages.reset)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OwnerWorkspaceFiltersToolbar;
